"use client"

import { useAuth } from "@/lib/auth/context"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { LandingHero } from "@/components/landing-hero"
import { AppLoadingShell } from "@/components/app-loading-shell"
import { FilterSidebar, FilterState } from "@/components/filter-sidebar"
import { SurfSpotGrid } from "@/components/surf-spot-grid"
import { MapView } from "@/components/map-view"
import { SpotsToolbar } from "@/components/spots-toolbar"
import { PreferencesButton } from "@/components/preferences-button"
import { LegalCornerButton } from "@/components/legal-corner-button"
import { useGeolocation } from "@/hooks/use-geolocation"
import { usePlatform } from "@/hooks/use-platform"
import { cn } from "@/lib/utils"

export default function HomePage() {
  const { user, loading } = useAuth()
  const router = useRouter()
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid")
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<FilterState | null>(null)
  const [nearMeEnabled, setNearMeEnabled] = useState(false)
  const [nearMeRadius, setNearMeRadius] = useState(50)
  const { latitude, longitude, error: geoError, loading: geoLoading, requestLocation, clearLocation } = useGeolocation()
  const { isMobile, isCapacitor, isNative, isStandalone, isLoaded, isIOS } = usePlatform()

  // Detect if we are in "App Mode" (Mobile only for home page redirect)
  // For iOS, we only consider it "App Mode" if it's the native app (Native)
  // We no longer support/encourage the iOS PWA experience since we have a native app
  const isAppMode = isMobile && (
    isNative || 
    (isStandalone && !isIOS) // Allow Android PWA but skip iOS PWA
  )
  
  // Detect if web user is logged in
  // For mobile users, we wait until isLoaded is true to be sure about isNative
  const shouldRedirectWebUser = isLoaded && !loading && user && !isAppMode

  useEffect(() => {
    if (isLoaded) {
      if (isAppMode) {
        console.log("[HomePage] App mode detected, redirecting to /home")
        router.push("/home")
        return
      }

      if (shouldRedirectWebUser) {
        console.log("[HomePage] Logged in web user detected, redirecting to /home for mobile or /explore for desktop")
        // On mobile, even if it's the web browser, let's just show the dashboard if logged in
        if (isMobile) {
          router.push("/home")
        } else {
          router.push("/explore")
        }
      }
    }
  }, [isLoaded, isAppMode, shouldRedirectWebUser, router, isIOS, isMobile, isNative, isCapacitor, isStandalone])

  const handleNearMeToggle = (enabled: boolean) => {
    if (enabled) {
      requestLocation()
      setNearMeEnabled(true)
    } else {
      clearLocation()
      setNearMeEnabled(false)
    }
  }
  
  const handleFiltersApply = (filters: FilterState) => {
    setAppliedFilters(filters)
  }

  if (loading || !isLoaded || isAppMode || shouldRedirectWebUser) {
    return <AppLoadingShell />
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-foreground flex flex-col">
      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-teal-500/10 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 flex flex-col flex-1">
        <LandingHero />

        <div className="container mx-auto px-4 md:px-6">
          <div className="mt-[-4rem] mb-12 relative z-20">
            <SpotsToolbar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onFilterToggle={() => setShowMobileFilters(!showMobileFilters)}
              showFilters={showMobileFilters}
            />
          </div>

          <div className="flex gap-8 relative pb-20">
            <FilterSidebar
              showMobileFilters={showMobileFilters}
              setShowMobileFilters={setShowMobileFilters}
              onNearMeToggle={handleNearMeToggle}
              nearMeEnabled={nearMeEnabled}
              nearMeLoading={geoLoading}
              nearMeError={geoError}
              nearMeRadius={nearMeRadius}
              onNearMeRadiusChange={setNearMeRadius}
              onFiltersApply={handleFiltersApply}
            />
            
            <main className={cn(
              "flex-1 transition-all duration-500",
              showMobileFilters ? "lg:ml-80" : "",
              viewMode === "map" ? "h-[70vh] rounded-[3rem] overflow-hidden shadow-2xl border border-white/20 dark:border-slate-800" : ""
            )}>
              {viewMode === "grid" ? (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                  <SurfSpotGrid
                    userLatitude={nearMeEnabled ? latitude : null}
                    userLongitude={nearMeEnabled ? longitude : null}
                    maxDistance={nearMeEnabled ? nearMeRadius : null}
                    sortByDistance={nearMeEnabled}
                  />
                </div>
              ) : (
                <div className="h-full w-full">
                  <MapView />
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  )
}
